import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Flex, 
  Image, 
  IconButton, 
  Drawer, 
  DrawerBody, 
  DrawerOverlay, 
  DrawerContent, 
  DrawerCloseButton, 
  Link as ChakraLink, 
  useColorModeValue, 
  useDisclosure,
  Text,
  Button,
  useColorMode,
  VStack,
  Portal,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody
} from '@chakra-ui/react';
import { NavLink, Link } from 'react-router-dom';
import Logo from '../media/logo_transparent.png';
import { HamburgerIcon, MoonIcon, SunIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import { FaCross, FaPray, FaHandsHelping, FaImages, FaUsers } from 'react-icons/fa';

const Navbar = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [placement] = useState('right');
  const { colorMode, toggleColorMode } = useColorMode();
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // ===== UPDATED COLOR SCHEME =====
  // For DARK MODE - Super bright & sharp
  const darkBlue = 'blue.400';  // Bright electric blue
  const darkRed = 'red.400';    // Vivid crimson red

  // For LIGHT MODE - Super dark & rich
  const lightBlue = 'blue.900'; // Deep navy blue
  const lightRed = 'red.800';   // Dark blood red

  // Apply them
  const primaryColor = useColorModeValue(lightBlue, darkBlue);
  const secondaryColor = useColorModeValue(lightRed, darkRed);
  
  // Vibrant colors for youth organization
  const color = useColorModeValue('gray.800', 'white');
  const hoverBgColor = useColorModeValue('blue.100', 'blue.800'); // Changed from purple to blue
  const bg = useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(26, 32, 44, 0.95)');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const menuBg = useColorModeValue('white', 'gray.800');
  const menuItemHoverBg = useColorModeValue('blue.50', 'blue.900'); // Changed from purple to blue
  const glowColor = useColorModeValue('blue.200', 'blue.600'); // Changed from purple to blue
  // ===== END COLOR SCHEME =====

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const mainLinks = [
    { path: '/', name: 'Home', icon: <FaCross style={{ marginRight: '8px' }} /> },
    { 
      path: '/about', 
      name: 'About',
      icon: <FaPray style={{ marginRight: '8px' }} />,
      subLinks: [
        { path: '/about#team', name: 'Our Team', icon: <FaUsers style={{ marginRight: '8px' }} /> },
        { path: '/about#history', name: 'Our History', icon: <FaHandsHelping style={{ marginRight: '8px' }} /> },
        { path: '/about#core-values', name: 'Our Beliefs', icon: <FaCross style={{ marginRight: '8px' }} /> }
      ]
    },
    { path: '/events', name: 'Events', icon: <FaHandsHelping style={{ marginRight: '8px' }} /> },
    { path: '/contact', name: 'Contact', icon: <FaUsers style={{ marginRight: '8px' }} /> },
  ];

  const dropdownLinks = [
    { path: '/donations', name: 'Donations', icon: <FaHandsHelping style={{ marginRight: '8px' }} /> },
    { path: '/gallery', name: 'Gallery', icon: <FaImages style={{ marginRight: '8px' }} /> },
  ];

  const allLinks = [...mainLinks, ...dropdownLinks];

  const renderDesktopNav = () => (
    <Flex display={{ base: 'none', md: 'flex' }} align="center" gap={2}>
      {mainLinks.map((link) => (
        link.subLinks ? (
          <Popover key={link.path} trigger="hover" placement="bottom-start">
            <PopoverTrigger>
              <Button
                as={NavLink}
                to={link.path}
                px={4}
                py={2}
                variant="ghost"
                fontWeight="semibold"
                fontSize="lg"
                leftIcon={link.icon}
                rightIcon={<ChevronDownIcon />}
                _hover={{ 
                  bg: hoverBgColor,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${glowColor}`,
                }}
                _activeLink={{
                  color: colorMode === 'light' ? 'blue.600' : 'blue.300', // Changed from purple to blue
                  fontWeight: 'bold',
                }}
              >
                {link.name}
              </Button>
            </PopoverTrigger>
            <PopoverContent 
              border="none" 
              boxShadow="xl"
              bg={menuBg}
              width="auto"
              minWidth="200px"
            >
              <PopoverBody p={2}>
                <VStack align="stretch" spacing={1}>
                  {link.subLinks.map((subLink) => (
                    <Button
                      key={subLink.path}
                      as={NavLink}
                      to={subLink.path}
                      variant="ghost"
                      justifyContent="flex-start"
                      leftIcon={subLink.icon}
                      fontWeight="medium"
                      _hover={{
                        bg: menuItemHoverBg,
                        transform: 'scale(1.02)',
                      }}
                      _activeLink={{
                        bg: menuItemHoverBg,
                        color: colorMode === 'light' ? 'blue.600' : 'blue.300', // Changed from purple to blue
                        fontWeight: 'semibold',
                      }}
                    >
                      {subLink.name}
                    </Button>
                  ))}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        ) : (
          <Button
            key={link.path}
            as={NavLink}
            to={link.path}
            px={4}
            py={2}
            variant="ghost"
            fontWeight="semibold"
            fontSize="lg"
            leftIcon={link.icon}
            _hover={{ 
              bg: hoverBgColor,
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${glowColor}`,
            }}
            _activeLink={{
              color: colorMode === 'light' ? 'blue.600' : 'blue.300', // Changed from purple to blue
              fontWeight: 'bold',
            }}
          >
            {link.name}
          </Button>
        )
      ))}

      {/* More Dropdown Menu */}
      <Menu 
        isOpen={isMenuOpen}
        onOpen={() => setIsMenuOpen(true)}
        onClose={() => setIsMenuOpen(false)}
      >
        <MenuButton
          as={Button}
          px={4}
          py={2}
          variant="ghost"
          fontWeight="semibold"
          fontSize="lg"
          rightIcon={<ChevronDownIcon />}
          _hover={{ 
            bg: hoverBgColor,
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${glowColor}`,
          }}
          _active={{ bg: hoverBgColor }}
          _expanded={{ bg: hoverBgColor }}
        >
          More
        </MenuButton>
        <MenuList 
          bg={menuBg}
          borderColor={borderColor}
          boxShadow="xl"
          minW="200px"
          py={1}
        >
          {dropdownLinks.map((link) => (
            <MenuItem 
              key={link.path}
              as={NavLink}
              to={link.path}
              icon={link.icon}
              _hover={{ 
                bg: menuItemHoverBg,
                transform: 'scale(1.02)',
              }}
              _activeLink={{
                bg: menuItemHoverBg,
                color: colorMode === 'light' ? 'blue.600' : 'blue.300', // Changed from purple to blue
                fontWeight: 'semibold',
              }}
            >
              {link.name}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <IconButton
        ml={2}
        aria-label="Toggle color mode"
        icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
        onClick={toggleColorMode}
        variant="ghost"
        borderRadius="full"
        fontSize="lg"
        _hover={{ 
          bg: hoverBgColor,
          transform: 'scale(1.1)',
          boxShadow: `0 0 10px ${glowColor}`,
        }}
      />
    </Flex>
  );

  const renderMobileNav = () => (
    <Flex display={{ md: 'none' }} align="center">
      <IconButton
        aria-label="Toggle color mode"
        icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
        onClick={toggleColorMode}
        variant="ghost"
        borderRadius="full"
        mr={2}
        _hover={{ bg: hoverBgColor }}
      />
      <IconButton
        aria-label="Open Menu"
        size="lg"
        variant="ghost"
        icon={<HamburgerIcon />}
        onClick={onOpen}
        _hover={{ bg: hoverBgColor }}
      />
    </Flex>
  );

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={999}
      backdropFilter="blur(10px)"
      boxShadow={scrolled ? 'lg' : 'none'}
      transition="all 0.3s ease"
      borderBottomWidth="1px"
      borderBottomColor={borderColor}
    >
      <Flex
        align="center"
        justify="space-between"
        bg={bg}
        color={color}
        p={4}
        maxW="1800px"
        mx="auto"
        px={[4, 6, 8, 10]}
      >
        {/* Logo with animation */}
        <ChakraLink 
          as={Link} 
          to="/" 
          display="flex" 
          alignItems="center" 
          _hover={{ textDecoration: 'none' }}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 10 }}
          >
            <Image
              src={Logo}
              alt="logo"
              boxSize={['60px', '70px', '80px']}
              objectFit="contain"
              filter={colorMode === 'dark' ? 'brightness(1.1)' : 'none'}
            />
          </motion.div>
          <Text 
            fontSize={['lg', 'xl', '2xl']} 
            fontWeight="extrabold"
            ml={3}
            bgGradient={`linear(to-r, ${primaryColor}, ${secondaryColor})`} // Updated gradient
            bgClip="text"
            textShadow={colorMode === 'dark' ? '0 0 8px rgba(59, 130, 246, 0.6)' : 'none'} // Blue glow
          >
            ALPHA YOUTH UK
          </Text>
        </ChakraLink>

        {renderDesktopNav()}
        {renderMobileNav()}

        {/* Mobile Drawer with improved styling */}
        <Portal>
          <Drawer 
            placement={placement} 
            onClose={onClose} 
            isOpen={isOpen} 
            size="full"
          >
            <DrawerOverlay backdropFilter="blur(5px)" />
            <DrawerContent 
              bgGradient={`linear(to-b, ${primaryColor}, gray.900)`} // Updated gradient
              color="white"
            >
              <DrawerCloseButton
                size="lg"  
                right={4}  
                top={4}
                color="white"
                _hover={{ color: 'red.300', transform: 'scale(1.1)' }} // Changed to red
              />
              
              <DrawerBody 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center"
                pt={20}
              >
                <VStack spacing={6} w="full">
                  {allLinks.map((link) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <Button
                        as={NavLink}
                        to={link.path}
                        w="full"
                        size="lg"
                        variant="ghost"
                        justifyContent="flex-start"
                        leftIcon={link.icon}
                        fontSize="xl"
                        fontWeight="semibold"
                        color="whiteAlpha.900"
                        _hover={{
                          bg: 'whiteAlpha.100',
                          transform: 'translateX(5px)',
                        }}
                        _activeLink={{
                          bg: 'whiteAlpha.200',
                          color: 'red.300', // Changed to red
                          fontWeight: 'bold',
                        }}
                        onClick={onClose}
                      >
                        {link.name}
                      </Button>
                    </motion.div>
                  ))}
                </VStack>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </Portal>
      </Flex>
    </Box>
  );
};

export default Navbar;